// TranscriptDisplay.js
import React, { useEffect, useRef, useState } from 'react';

const TranscriptDisplay = ({ transcription, interimTranscription, isRecording, speakerLabels = {}, updateSpeakerLabel = () => { } }) => {
  const boxRef = useRef(null);
  const autoScrollRef = useRef(true);
  const rafRef = useRef(null);

  const [editingSpeaker, setEditingSpeaker] = useState(null);

  const renderParagraph = (text) => {
    if (!text) return text;

    const speakerRegex =
      /(^|\n)\s*(?:>>\s*)?(?:\[(S\d+)\]|(S\d+):)\s*/g;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = speakerRegex.exec(text)) !== null) {
      const speakerId = match[2] || match[3];
      const displayName = speakerLabels?.[speakerId] || speakerId;

      // Add any normal text before this speaker label
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Preserve newline if speaker label started on a new line
      if (match[1] === "\n") {
        parts.push("\n");
      }

      const renameSpeaker = () => {
        setEditingSpeaker({
          speakerId,
          value: speakerLabels?.[speakerId] || speakerId
        });
      };

      parts.push(
        <span
          key={`speaker-${match.index}-${speakerId}`}
          className="speaker-label"
          onClick={renameSpeaker}
          title={`Rename ${speakerId}`}
        >
          {displayName}:{" "}
        </span>
      );

      lastIndex = speakerRegex.lastIndex;
    }

    // Add remaining text after the last speaker label
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    // If no speaker labels were found, return the original text
    return parts.length > 0 ? parts : text;
  };

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const handleScroll = () => {
      const distanceFromBottom =
        box.scrollHeight - box.clientHeight - box.scrollTop;

      autoScrollRef.current = distanceFromBottom <= 40;
    };

    box.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      box.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (isRecording) {
      autoScrollRef.current = true;
    }
  }, [isRecording]);

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    if (!isRecording) return;
    if (!autoScrollRef.current) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      box.scrollTop = box.scrollHeight;
    });
  }, [transcription, interimTranscription, isRecording]);

  const paragraphs = transcription ? transcription.split(/\n\n+/) : [];
  const lastIndex = paragraphs.length - 1;

  let trimmedInterim = interimTranscription || "";
  let interimIsNewParagraph = false;

  if (trimmedInterim.startsWith("\n")) {
    interimIsNewParagraph = true;
    trimmedInterim = trimmedInterim.replace(/^\n+/, "");
  }

  const saveSpeakerEdit = () => {
    if (!editingSpeaker) return;

    const cleaned = editingSpeaker.value.trim();

    updateSpeakerLabel(
      editingSpeaker.speakerId,
      cleaned || editingSpeaker.speakerId
    );

    setEditingSpeaker(null);
  };

  const cancelSpeakerEdit = () => {
    setEditingSpeaker(null);
  };

  return (
    <div ref={boxRef} className="strings-container">
      {paragraphs.map((para, idx) => (
        <div key={idx} className="string-item">
          {renderParagraph(para)}
          {idx === lastIndex && trimmedInterim && !interimIsNewParagraph && (
            <span className="interim">{renderParagraph(trimmedInterim)}</span>
          )}
        </div>
      ))}

      {paragraphs.length === 0 && trimmedInterim && !interimIsNewParagraph && (
        <div className="string-item">
          <span className="interim">{renderParagraph(trimmedInterim)}</span>
        </div>
      )}

      {trimmedInterim && interimIsNewParagraph && (
        <div className="string-item">
          <span className="interim">{renderParagraph(trimmedInterim)}</span>
        </div>
      )}

      <div className="scroll-bottom-spacer" />
      {editingSpeaker && (
        <div className="speaker-editor">
          <div className="speaker-editor-title">
            Rename {editingSpeaker.speakerId}
          </div>

          <input
            className="speaker-editor-input"
            value={editingSpeaker.value}
            autoFocus
            onChange={(e) =>
              setEditingSpeaker((prev) => ({
                ...prev,
                value: e.target.value
              }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                saveSpeakerEdit();
              }

              if (e.key === "Escape") {
                cancelSpeakerEdit();
              }
            }}
          />

          <div className="speaker-editor-actions">
            <button type="button" onClick={saveSpeakerEdit}>
              Save
            </button>
            <button type="button" onClick={cancelSpeakerEdit}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptDisplay;